import React, { useCallback, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { FileUp, FolderUp, Loader2, UploadCloud } from 'lucide-react';
import { collectEntries, type FsEntryLike, type PickedFile } from '@services/fileEntryUtils';

export type { PickedFile } from '@services/fileEntryUtils';

interface DropzoneProps {
  /**
   * Arquivos escolhidos/soltos. `relativePath` = caminho dentro da pasta (com o nome do arquivo)
   * quando vieram de uma pasta; '' para arquivos soltos. `unreadable` = caminhos que o navegador
   * não conseguiu ler na varredura de pastas.
   */
  onFiles: (files: PickedFile[], unreadable: string[]) => void;
  accept?: string;
  multiple?: boolean;
  /** Mostra o botão "Escolher pasta" e aceita pastas no arrastar-e-soltar (com subpastas). */
  allowFolders?: boolean;
  disabled?: boolean;
  title?: string;
  hint?: React.ReactNode;
  className?: string;
}

const fromFileList = (list: FileList | null): PickedFile[] =>
  Array.from(list ?? []).map((file) => ({ file, relativePath: file.webkitRelativePath || '' }));

/** `webkitdirectory`/`directory` não estão nos tipos do React: liga pelo ref. */
const directoryInputRef = (el: HTMLInputElement | null) => {
  if (!el) return;
  el.setAttribute('webkitdirectory', '');
  el.setAttribute('directory', '');
};

/** Área de arrastar-e-soltar (ou clicar) para escolher arquivos — e, opcionalmente, pastas. */
export const Dropzone: React.FC<DropzoneProps> = ({
  onFiles,
  accept = '*/*',
  multiple = true,
  allowFolders = false,
  disabled = false,
  title,
  hint = 'Arquivos PDF, imagens e planilhas.',
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [scanning, setScanning] = useState<number | null>(null);

  const blocked = disabled || scanning !== null;
  const heading =
    title ??
    (allowFolders
      ? 'Arraste arquivos ou pastas aqui, ou use os botões abaixo'
      : 'Arraste os arquivos aqui ou clique para escolher');

  const emit = useCallback(
    (files: PickedFile[], unreadable: string[] = []) => {
      if ((files.length === 0 && unreadable.length === 0) || disabled) return;
      onFiles(multiple ? files : files.slice(0, 1), unreadable);
    },
    [disabled, multiple, onFiles],
  );

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (blocked) return;
    const dt = e.dataTransfer;
    if (!allowFolders || !dt.items || dt.items.length === 0) {
      emit(fromFileList(dt.files));
      return;
    }
    // As entradas PRECISAM ser obtidas aqui, de forma síncrona: depois do evento o
    // DataTransfer é esvaziado pelo navegador.
    const entries: FsEntryLike[] = [];
    const loose: PickedFile[] = [];
    Array.from(dt.items).forEach((item) => {
      if (item.kind !== 'file') return;
      const entry = typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null;
      if (entry) entries.push(entry as unknown as FsEntryLike);
      else {
        const file = item.getAsFile();
        if (file) loose.push({ file, relativePath: '' });
      }
    });
    if (entries.length === 0) {
      emit(loose.length > 0 ? loose : fromFileList(dt.files));
      return;
    }
    setScanning(0);
    collectEntries<File>(entries, (n) => setScanning(n))
      .then(({ files, unreadable }) => emit([...loose, ...files], unreadable))
      .catch(() => emit(loose))
      .finally(() => setScanning(null));
  };

  const pickFiles = () => !blocked && inputRef.current?.click();
  const pickFolder = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!blocked) folderRef.current?.click();
  };

  return (
    <div
      role="button"
      tabIndex={blocked ? -1 : 0}
      aria-disabled={blocked}
      onClick={pickFiles}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !blocked) {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!blocked) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      className={clsx(
        'flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors',
        blocked
          ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
          : dragging
            ? 'border-emerald-500 bg-emerald-50 text-emerald-700 cursor-copy'
            : 'border-slate-300 bg-white text-slate-600 hover:border-emerald-400 hover:bg-emerald-50/40 cursor-pointer',
        className,
      )}
    >
      {scanning !== null ? (
        <>
          <Loader2 size={32} className="text-emerald-600 animate-spin" />
          <p className="text-sm font-medium text-slate-700">
            Lendo pastas… {scanning > 0 && `${scanning.toLocaleString('pt-BR')} arquivos encontrados`}
          </p>
        </>
      ) : (
        <>
          <UploadCloud size={32} className={dragging ? 'text-emerald-600' : 'text-slate-400'} />
          <p className="text-sm font-medium">{heading}</p>
          {hint && <div className="text-small">{hint}</div>}
          {allowFolders && (
            <div className="flex flex-wrap justify-center gap-2 mt-1">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
                onClick={(e) => {
                  e.stopPropagation();
                  pickFiles();
                }}
                disabled={blocked}
              >
                <FileUp size={16} /> Escolher arquivos
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800"
                onClick={pickFolder}
                disabled={blocked}
              >
                <FolderUp size={16} /> Escolher pasta
              </button>
            </div>
          )}
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          emit(fromFileList(e.target.files));
          e.target.value = '';
        }}
      />
      {allowFolders && (
        <input
          ref={(el) => {
            folderRef.current = el;
            directoryInputRef(el);
          }}
          type="file"
          className="hidden"
          multiple
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            emit(fromFileList(e.target.files));
            e.target.value = '';
          }}
        />
      )}
    </div>
  );
};

export default Dropzone;
